import type { IField, IMergeValues, ITextContentDoc } from '@getaccept/editor-lib';
import { EFieldType, EMergeCategory, EMergeKey, EProsemirrorType } from '@getaccept/editor-lib';
import type { Editor as TipTapEditor, NodeWithPos } from '@tiptap/vue-2';
import { findChildren } from '@tiptap/vue-2';
import type { EditorState, Transaction } from 'prosemirror-state';
import type { Node as ProsemirrorNode } from 'prosemirror-model';
import moment from 'moment';

interface SetContentPayload {
  viewer: TipTapEditor;
  content: ITextContentDoc;
  fields: IField[];
  mergeValues: IMergeValues;
}

export const setContent = ({ viewer, content, fields, mergeValues }: SetContentPayload) => {
  if (viewer.view.hasFocus() || JSON.stringify(viewer.getJSON()) === JSON.stringify(content)) {
    return;
  }
  updateMergeValues({ viewer, content, fields, mergeValues });
};

export const updateMergeValues = ({ viewer, content, fields, mergeValues }: SetContentPayload) => {
  viewer.commands.setContent(content);
  viewer.view?.dispatch(replaceMergeTagsWithText(viewer.view.state, fields, mergeValues));
};

export const handleInit = (viewer: TipTapEditor, fields: IField[], mergeValues: IMergeValues) => {
  viewer.view?.dispatch(replaceMergeTagsWithText(viewer.state, fields, mergeValues));
};

export const replaceMergeTagsWithText = (
  state: EditorState,
  fields: IField[],
  mergeValues: IMergeValues
): Transaction => {
  const nodes: NodeWithPos[] = findChildren(
    state.doc,
    (node: ProsemirrorNode) => node.type.name === EProsemirrorType.MergeTag
  ).reverse();

  let transaction = state.tr;

  nodes.forEach(({ node, pos }: NodeWithPos) => {
    const mergeValue = getMergeValue(node, fields, mergeValues);

    if (mergeValue) {
      transaction = transaction.replaceWith(
        pos,
        pos + node.nodeSize,
        state.schema.text(mergeValue, node.marks)
      );
    }
  });

  return transaction;
};

export const getTagById = (tagId: string, fields: IField[]): IField | undefined => {
  return fields.find(({ id }: IField) => id === tagId);
};

export const getRecipientValue = (
  recipientId: string,
  mergeKey: EMergeKey,
  mergeValues: IMergeValues
): string => {
  const recipient = mergeValues?.recipients?.find(({ id }) => recipientId === id);

  if (!recipient || (!recipient.email && !recipient.mobile)) {
    return '';
  }

  return recipient?.[mergeKey] || '';
};

export const getEntityValue = (mergeKey: EMergeKey, mergeValues: IMergeValues): string => {
  return mergeValues?.entity?.[mergeKey] || '';
};

export const getSenderValue = (mergeKey: EMergeKey, mergeValues: IMergeValues): string => {
  return mergeValues?.sender?.[mergeKey] || '';
};

export const getDocumentValue = (mergeKey: EMergeKey, mergeValues: IMergeValues): string => {
  const value: string | number = mergeValues?.document?.[mergeKey];

  if (!value) {
    return '';
  }

  if (mergeKey === EMergeKey.sendDate || mergeKey === EMergeKey.expirationDate) {
    return moment(value).format('LLL');
  }

  if (typeof value === 'number' && mergeKey === EMergeKey.value) {
    return String(value.toFixed(0));
  }

  return String(value);
};

export const getMergeValue = (
  node: ProsemirrorNode,
  fields: IField[],
  mergeValues: IMergeValues
): string => {
  const { tagId, category, mergeKey } = node.attrs as {
    tagId: string;
    category: EMergeCategory;
    mergeKey: EMergeKey;
  };

  const currentTag: IField = getTagById(tagId, fields);

  if (currentTag?.type === EFieldType.Custom) {
    return currentTag.value || '';
  }

  switch (category) {
    case EMergeCategory.Recipient:
      return getRecipientValue(currentTag.recipientId, mergeKey, mergeValues);

    case EMergeCategory.Entity:
      return getEntityValue(mergeKey, mergeValues);

    case EMergeCategory.Sender:
      return getSenderValue(mergeKey, mergeValues);

    case EMergeCategory.Document:
      return getDocumentValue(mergeKey, mergeValues);

    default:
      return '';
  }
};
